import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import Topbar from '../../components/shared/Topbar';
import TenantSidebar from '../../components/tenant/TenantSidebar';
import LegalFooter from '../../components/shared/LegalFooter';
import '../../styles/global.css';

const fmt = (n) => parseFloat(n || 0).toFixed(2);

// Same logic as SignupPage.js — only "real" when retail beats the bulk price.
const hasRealSavings = (plan) =>
  plan != null &&
  plan.retail_price != null &&
  parseFloat(plan.retail_price) > parseFloat(plan.bulknet_price) &&
  (parseFloat(plan.savings_per_month || 0) > 0);

export default function TenantPlan() {
  const [subscription, setSubscription] = useState(null);
  const [doublePlan, setDoublePlan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const subRes = await api.get('/billing/tenant/subscription');
        const sub = subRes.data.subscription;
        if (cancelled) return;
        setSubscription(sub);

        // Only need an upsell target when the tenant is on Single Play.
        if (sub?.tier === 'single') {
          try {
            const meRes = await api.get('/tenant/me');
            const slug = meRes.data.tenant?.property_slug;
            if (slug) {
              const planRes = await api.get(`/plans/property/${slug}`);
              const dbl = (planRes.data.plans || []).find(p => p.tier === 'double');
              if (!cancelled && dbl) setDoublePlan(dbl);
            }
          } catch {
            // Upsell is non-critical — ignore failures and just hide the CTA.
          }
        }
      } catch {
        if (!cancelled) toast.error('Could not load plan information');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  const features = subscription?.features;
  const featureList = Array.isArray(features) ? features : [];

  return (
    <div className="app-layout">
      <Topbar role="tenant" />
      <div className="dashboard-layout">
        <TenantSidebar />
        <main id="main-content" className="main-content">
          <div className="page-title">My Plan</div>
          <div className="page-sub">Your current internet service plan.</div>

          {subscription ? (
            <div className="card" style={{ marginBottom: 24 }}>
              <div className="card-header">
                <div className="card-title">Current Plan</div>
                <span className={`badge badge-${subscription.status === 'active' ? 'green' : 'orange'}`}>
                  {subscription.cancel_at_period_end ? 'Cancelling' : subscription.status}
                </span>
              </div>
              <div className="card-body">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 20 }}>
                  <div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--navy)' }}>{subscription.plan_name}</div>
                    {subscription.speed_mbps && (
                      <div style={{ fontSize: 13, color: 'var(--text-sub)', marginTop: 2 }}>{subscription.speed_mbps} Mbps · Unlimited data</div>
                    )}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 32, fontWeight: 800, color: 'var(--navy)' }}>
                      ${fmt(subscription.bulknet_price)}<span style={{ fontSize: 14, fontWeight: 400, color: 'var(--text-sub)' }}>/mo</span>
                    </div>
                    {hasRealSavings(subscription) && (
                      <div style={{ fontSize: 13, color: 'var(--text-sub)', marginTop: 2 }}>
                        Retail: <span style={{ textDecoration: 'line-through' }}>${fmt(subscription.retail_price)}/mo</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Savings callout */}
                {hasRealSavings(subscription) && (
                  <div style={{
                    background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 10,
                    padding: '12px 16px', marginTop: 20, display: 'flex', alignItems: 'center', gap: 10,
                  }}>
                    <span style={{ fontSize: 20 }}>&#x2714;&#xFE0F;</span>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#065f46' }}>
                        You're saving ${fmt(subscription.savings_per_month)}/month compared to retail
                      </div>
                      <div style={{ fontSize: 12, color: '#047857' }}>
                        That's {subscription.savings_pct}% off — ${fmt(subscription.savings_per_year)} saved per year
                      </div>
                    </div>
                  </div>
                )}

                {/* Features */}
                {featureList.length > 0 && (
                  <div style={{ marginTop: 20 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy)', marginBottom: 8 }}>Plan includes:</div>
                    <ul style={{ margin: 0, paddingLeft: 20, listStyle: 'disc' }}>
                      {featureList.map((f, i) => (
                        <li key={i} style={{ fontSize: 13, color: 'var(--text-sub)', padding: '3px 0' }}>{f}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="card" style={{ marginBottom: 24 }}>
              <div className="card-body" style={{ textAlign: 'center', padding: '40px 24px' }}>
                <div style={{ fontSize: 36, marginBottom: 12 }}>&#x1F4E1;</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--navy)', marginBottom: 8 }}>
                  Your plan is being set up
                </div>
                <div style={{ fontSize: 14, color: 'var(--text-sub)', maxWidth: 400, margin: '0 auto' }}>
                  We'll notify you once your service is active. If you have questions, reach out to our support team.
                </div>
              </div>
            </div>
          )}

          {/* Upgrade section */}
          {subscription?.tier === 'single' && doublePlan ? (
            <div className="card">
              <div className="card-body" style={{ padding: '24px' }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--navy)', marginBottom: 4 }}>
                  Upgrade to Double Play
                </div>
                <p style={{ fontSize: 14, color: 'var(--text-sub)', margin: '0 0 16px', lineHeight: 1.6 }}>
                  Add Spectrum TV Select Plus + Xumo for just $18 more — save $176+/mo vs. $262 Spectrum retail.
                </p>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 12 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)' }}>{doublePlan.name}</div>
                  <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--navy)' }}>
                    ${fmt(doublePlan.bulknet_price)}<span style={{ fontSize: 13, fontWeight: 400, color: 'var(--text-sub)' }}>/mo</span>
                  </div>
                </div>

                <ul style={{ margin: '12px 0 0', paddingLeft: 20, listStyle: 'disc' }}>
                  {(Array.isArray(doublePlan.features) && doublePlan.features.length
                    ? doublePlan.features
                    : ['Spectrum TV Select Plus', '1 Xumo Box', '1 Gbps Internet', 'Home WiFi Modem/Router']
                  ).map((f, i) => (
                    <li key={i} style={{ fontSize: 13, color: 'var(--text-sub)', padding: '3px 0' }}>{f}</li>
                  ))}
                </ul>

                {hasRealSavings(doublePlan) && (
                  <div style={{
                    background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 10,
                    padding: '10px 14px', marginTop: 16, fontSize: 13, fontWeight: 700, color: '#065f46',
                  }}>
                    Compare to ${parseFloat(doublePlan.retail_price).toFixed(0)} retail — save ${fmt(doublePlan.savings_per_month)}/mo
                  </div>
                )}

                <button
                  className="btn btn-primary"
                  style={{ marginTop: 16, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  onClick={() => window.alert('Upgrade flow coming soon — contact info@affordable-telecom.com to upgrade today.')}
                >
                  Upgrade Now <span style={{ fontSize: 14 }}>&rarr;</span>
                </button>
              </div>
            </div>
          ) : subscription?.tier === 'double' ? (
            <div className="card">
              <div className="card-body" style={{ padding: '24px' }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', marginBottom: 8 }}>
                  You're on our top plan.
                </div>
                <p style={{ fontSize: 14, color: 'var(--text-sub)', margin: 0, lineHeight: 1.6 }}>
                  Spectrum Gig Internet, Spectrum TV Select Plus, and 1 Xumo Box — all locked in.
                </p>
              </div>
            </div>
          ) : null}

          <LegalFooter />
        </main>
      </div>
    </div>
  );
}
